import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { LuCheck, LuX, LuRotateCcw, LuArrowRight, LuMessageCircle } from "react-icons/lu";
import StarIcon from "../../../components/ui/StarIcon";
import { Hint, mediaUrl, shuffleStable } from "../common";
import * as s from "./blocks.styled";
import { EXTRA_BLOCKS, Marked, WordBox } from "./extra";
import Celebrate from "./Celebrate";
import { RICH_BLOCKS, BgTalk } from "./rich";
import { KIDS_BLOCKS } from "./kids";
import { useReport, useSlideState, useSharedState, useReviewMode, addTry } from "../answers";
import { useT } from "../i18n";

const norm = (value = "") => value.trim().toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, " ");

// ---------- cover ----------
function Cover({ slide, lesson }) {
  const t = useT();
  const { subtitle, goals = [] } = slide.data || {};
  return (
    <s.Cover>
      <s.CoverText>
        <s.CoverBadge>{lesson?.course?.endsWith("-trial") ? t("Trial lesson") : `${t("Lesson")} ${lesson?.number}`}</s.CoverBadge>
        <s.CoverTitle>{slide.title}</s.CoverTitle>
        {subtitle && <s.CoverSubtitle>{subtitle}</s.CoverSubtitle>}
        {goals.length > 0 && (
          <s.Goals>
            <s.GoalsTitle>{t("Today you will")}</s.GoalsTitle>
            {goals.map((goal) => (
              <s.Goal key={goal}>
                <s.GoalIcon>
                  <LuCheck size={18} />
                </s.GoalIcon>
                {goal}
              </s.Goal>
            ))}
          </s.Goals>
        )}
      </s.CoverText>
      {slide.image?.url && (
        <s.CoverImage>
          <img src={mediaUrl(slide.image.url)} alt="" />
          {slide.image.credit && <s.Credit>{slide.image.credit}</s.Credit>}
        </s.CoverImage>
      )}
    </s.Cover>
  );
}
Cover.fullBleed = true;

// ---------- talk ----------
// data.phrases — фрази-підказки в окремому боксі під питаннями (напр. «In my opinion…»), data.phrasesTitle — підпис боксу
function Talk({ slide }) {
  const { questions = [], phrases = [], phrasesTitle = "Use the phrases" } = slide.data || {};
  if (slide.data?.bg && slide.image?.url) return <BgTalk slide={slide} />;
  return (
    <s.Split $withImage={Boolean(slide.image?.url)}>
      {slide.image?.url && <SideImage image={slide.image} />}
      <s.Questions>
        {questions.map((q) => (
          <s.Question key={q.text}>
            <s.QuestionIcon>
              <LuMessageCircle size={24} />
            </s.QuestionIcon>
            <Hint ua={q.ua}>{q.text}</Hint>
          </s.Question>
        ))}
        {phrases.length > 0 && (
          <s.PhraseBox>
            <b>{phrasesTitle}</b>
            <div>
              {phrases.map((phrase) => (
                <span key={phrase}>{phrase}</span>
              ))}
            </div>
          </s.PhraseBox>
        )}
      </s.Questions>
    </s.Split>
  );
}

function SideImage({ image }) {
  return (
    <s.SideImage $contain={image.fit === "contain"}>
      <img src={mediaUrl(image.url)} alt="" />
      {image.credit && <s.Credit>{image.credit}</s.Credit>}
    </s.SideImage>
  );
}

// ---------- match_images ----------
// Сітка фото без розтягування: підбираємо кількість колонок так, щоб плитки 4:3 були якомога більші
const TILE_RATIO = 4 / 3;
const GRID_GAP = 16;
function useTileGrid(count) {
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setBox({ w: entry.contentRect.width, h: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!box || !box.w || !box.h || !count) return { ref, style: undefined };
  let best = null;
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const maxW = (box.w - (cols - 1) * GRID_GAP) / cols;
    const maxH = (box.h - (rows - 1) * GRID_GAP) / rows;
    const w = Math.min(maxW, maxH * TILE_RATIO);
    if (w > 0 && (!best || w > best.w)) best = { cols, w };
  }
  if (!best) return { ref, style: undefined };
  const w = Math.floor(best.w);
  return {
    ref,
    style: {
      gridTemplateColumns: `repeat(${best.cols}, ${w}px)`,
      gridAutoRows: `${Math.floor(w / TILE_RATIO)}px`,
      justifyContent: "center",
      alignContent: "center",
    },
  };
}

function MatchImages({ slide, showAnswers }) {
  const t = useT();
  const { items = [], words = [] } = slide.data || {};
  const [picked, setPicked] = useState(null);
  const [assigned, setAssigned] = useSlideState(slide.id, "assigned", {});
  const grid = useTileGrid(items.length);
  const side = items.length <= 6 && words.length <= 6;
  const ChipBox = side ? s.SideChips : s.Chips;
  useReport(
    slide.id,
    items.filter((item, i) => assigned[i] && norm(assigned[i]) === norm(item.answer)).length,
    items.length,
    Object.keys(assigned).length
  );
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

  // Слово на фото можна прибрати (клік по фото без вибраного слова) або перенести на інше фото
  const assign = (index) => {
    if (showAnswers) return;
    if (!picked) {
      if (assigned[index] === undefined) return;
      setAssigned((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }
    setAssigned((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (v !== picked) next[k] = v;
      });
      next[index] = picked;
      return next;
    });
    setPicked(null);
  };

  // Клік по вже використаному слову — повертає його з фото й одразу вибирає (можна поставити на інше фото)
  const pickWord = (word) => {
    if (showAnswers) return;
    if (picked === word) {
      setPicked(null);
      return;
    }
    if (used.has(word)) {
      setAssigned((prev) => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          if (v !== word) next[k] = v;
        });
        return next;
      });
    }
    setPicked(word);
  };

  const used = new Set(Object.values(assigned));

  return (
    <s.MatchLayout $side={side}>
      <s.ImageGrid ref={grid.ref} $count={items.length} style={grid.style}>
        {items.map((item, i) => {
          const value = showAnswers ? item.answer : assigned[i];
          const state = value ? (norm(value) === norm(item.answer) ? "ok" : "bad") : null;
          return (
            <s.ImageTile
              key={item.image}
              onClick={() => assign(i)}
              $clickable={!showAnswers && (Boolean(picked) || assigned[i] !== undefined)}
              $state={state}
              title={!picked && assigned[i] !== undefined ? "Click to remove the word" : undefined}
            >
              <img src={mediaUrl(item.image)} alt="" />
              <s.TileLetter>{letters[i]}</s.TileLetter>
              {value && <s.TileWord $state={state} $small={items.length > 12}>{value}</s.TileWord>}
            </s.ImageTile>
          );
        })}
      </s.ImageGrid>
      <ChipBox>
        {words.map((word) => (
          <s.Chip
            $small={items.length > 12}
            key={word}
            onClick={() => pickWord(word)}
            $active={picked === word}
            $used={used.has(word) && !showAnswers}
          >
            {word}
          </s.Chip>
        ))}
        <s.Reset onClick={() => setAssigned({})} title={t("Reset")}>
          <LuRotateCcw size={20} />
        </s.Reset>
      </ChipBox>
    </s.MatchLayout>
  );
}

// ---------- match_pairs ----------
function MatchPairs({ slide, showAnswers }) {
  const { pairs = [] } = slide.data || {};
  const rights = useMemo(() => shuffleStable(pairs.map((p) => p.right), slide.id), [pairs, slide.id]);
  const [left, setLeft] = useState(null);
  const [links, setLinks] = useSlideState(slide.id, "links", {});
  // неправильні спроби по кожному лівому пункту — учитель бачить їх у зданому ДЗ
  const [tries, setTries] = useSlideState(slide.id, "tries", {});
  const review = useReviewMode();
  useReport(slide.id, pairs.filter((pair, i) => links[i] === pair.right).length, pairs.length, Object.keys(links).length);

  const chooseRight = (right) => {
    if (left === null) return;
    if (right !== pairs[left].right) setTries((prev) => addTry(prev, left, right));
    // праве слово може стояти лише в одній парі — звідти прибираємо
    setLinks((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([, value]) => value !== right));
      return { ...next, [left]: right };
    });
    setLeft(null);
  };
  // клік по вибраній відповіді — скасувати пару
  const unlink = (i) => setLinks((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => Number(key) !== i)));

  const shown = showAnswers ? Object.fromEntries(pairs.map((p, i) => [i, p.right])) : links;

  return (
    <s.Pairs>
      <s.PairColumn $compact={pairs.length > 6}>
        {pairs.map((pair, i) => {
          const value = shown[i];
          const wrongTries = !showAnswers && review ? tries[i] || [] : [];
          const state = value ? (value === pair.right ? (wrongTries.length ? "late" : "ok") : "bad") : null;
          return (
            <s.PairItem key={pair.left} onClick={() => setLeft(left === i ? null : i)} $active={left === i} $state={state}>
              <b>{i + 1}</b>
              <span>
                {pair.left}
                {wrongTries.length > 0 && <s.Tries title="Неправильні спроби учня">{wrongTries.join(" · ")}</s.Tries>}
              </span>
              {value && (
                <s.PairValue
                  $state={state}
                  title={showAnswers || review ? undefined : "Click to cancel"}
                  onClick={(event) => {
                    if (showAnswers || review) return;
                    event.stopPropagation();
                    unlink(i);
                  }}
                >
                  {value}
                </s.PairValue>
              )}
            </s.PairItem>
          );
        })}
      </s.PairColumn>
      <s.PairColumn $compact={pairs.length > 6}>
        {rights.map((right) => (
          <s.PairItem
            key={right}
            onClick={() => chooseRight(right)}
            $used={Object.values(shown).includes(right)}
            $clickable={left !== null}
          >
            {right}
          </s.PairItem>
        ))}
      </s.PairColumn>
    </s.Pairs>
  );
}

// ---------- cards ----------
function Cards({ slide }) {
  // columns — кількість колонок (напр. 9 для алфавіту), small — компактні картки без мінімальної висоти,
  // imageHeight — висота картинки в px (за замовчуванням 120; більша — для фото предметів)
  const { cards = [], columns, small, imageHeight } = slide.data || {};
  const [flipped, setFlipped] = useSharedState(slide.id, "flipped", {});
  return (
    <s.CardGrid $count={cards.length} $cols={columns} $small={small}>
      {cards.map((card, i) => (
        <s.Card key={card.front} onClick={() => setFlipped((prev) => ({ ...prev, [i]: !prev[i] }))} $flipped={flipped[i]} $small={small} $imgH={imageHeight}>
          {card.image && <img src={mediaUrl(card.image)} alt="" />}
          <s.CardText>{flipped[i] && card.back ? card.back : card.front}</s.CardText>
          {card.ua && !flipped[i] && <s.CardHint>{card.ua}</s.CardHint>}
        </s.Card>
      ))}
    </s.CardGrid>
  );
}

// ---------- fill_gaps ----------
// bank — слова в рамці: пропуски-слоти (лише перетягнути / клікнути). editable: true — слово з рамки можна дописати
// в пропуску (cook → cooks): перетягнути + змінити закінчення.
// {{word}} у тексті — приклад з книжки: показуємо як уже заповнений пропуск
const EXAMPLE = /\{\{([^}]+)\}\}/g;
function Example({ text }) {
  const t = useT();
  // **жирний** теж підтримується (як у choose)
  const parts = text.split(EXAMPLE);
  return (
    <span>
      {parts.map((part, i) => (i % 2 ? <s.ExampleGap key={i} title={t("Example")}>{part}</s.ExampleGap> : <Marked key={i} text={part} />))}
    </span>
  );
}

const GAP = /\[\[([^\]]+)\]\]/g;

const isRight = (answer, value) => answer.split("|").some((option) => norm(option) === norm(value));

// Пропуск: можна вписати, перетягнути слово з банку або клікнути слово → пропуск.
// Якщо value/onChange не передані — працює сам по собі (диктант).
function Gap({ answer, showAnswers, value: controlled, onChange, onFill, index, slotMode }) {
  const options = answer.split("|");
  const [own, setOwn] = useState("");
  const value = controlled !== undefined ? controlled : own;
  const setValue = onChange || setOwn;
  const [over, setOver] = useState(false);
  const [typing, setTyping] = useState(false);
  // Перевіряємо, коли учень закінчив вводити (вийшов з поля / Enter), а не на кожну літеру
  const state = value && !typing ? (isRight(answer, value) ? "ok" : "bad") : null;

  // Є банк слів — пропуск = слот (без вводу з клавіатури): слово ставиться кліком або перетягуванням,
  // клік по вставленому слову (без обраного в банку) або перетягування назад у банк — повертає його в банк.
  if (slotMode) {
    const shown = showAnswers ? options[0] : value;
    return (
      <s.GapInput
        as="span"
        $slot
        role="button"
        draggable={!showAnswers && !!value}
        onDragStart={(event) => {
          event.dataTransfer.setData("text/plain", value);
          event.dataTransfer.setData("application/x-gap", String(index));
        }}
        onClick={() => !showAnswers && onFill && onFill()}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          const word = event.dataTransfer.getData("text/plain");
          const from = event.dataTransfer.getData("application/x-gap");
          if (word && !showAnswers) onFill && onFill(word, from === "" ? null : Number(from));
        }}
        $state={showAnswers ? "ok" : value ? (isRight(answer, value) ? "ok" : "bad") : null}
        $over={over}
        $size={Math.max(4, ...options.map((option) => option.length + 1))}
        title={value ? "Click or drag back to the box" : "Drag a word here or click a word, then click here"}
      >
        {shown || " "}
      </s.GapInput>
    );
  }

  return (
    <s.GapInput
      value={showAnswers ? options[0] : value}
      onChange={(event) => setValue(event.target.value)}
      onFocus={() => setTyping(true)}
      onBlur={() => setTyping(false)}
      onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
      onClick={() => onFill && onFill()}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const word = event.dataTransfer.getData("text/plain");
        if (word) {
          setValue(word);
          setTyping(false);
        }
      }}
      $state={showAnswers ? "ok" : state}
      $over={over}
      // ширина — за основною відповіддю (довші варіанти на кшталт «have not been waiting» прокручуються в полі)
      $size={Math.max(4, options[0].length + 2)}
      readOnly={showAnswers}
      spellCheck={false}
    />
  );
}

function FillGaps({ slide, showAnswers }) {
  const t = useT();
  // dense — щільна таблиця (багато рядків), напр. країни й національності
  const { lines = [], bank = [], columns } = slide.data || {};
  const [values, setValues] = useSlideState(slide.id, "values", {});
  const [picked, setPicked] = useState(null);
  const usedWords = Object.values(values).map(norm);

  // Стабільні номери пропусків (не залежать від порядку рендеру колонок)
  const gapIds = {};
  let gapCount = 0;
  const gapAnswers = [];
  lines.forEach((line, i) =>
    (line.text || "").split(GAP).forEach((part, j) => {
      if (j % 2) {
        gapIds[`${i}-${j}`] = gapCount++;
        gapAnswers.push(part);
      }
    })
  );
  useReport(
    slide.id,
    gapAnswers.filter((answer, k) => values[k] && isRight(answer, values[k])).length,
    gapAnswers.length,
    Object.values(values).filter(Boolean).length
  );

  const groups = [];
  if (columns === 2) {
    // Є заголовки — колонка на кожен; немає — ділимо навпіл
    const keyed = lines.map((line, i) => ({ ...line, i }));
    // { break: true } — явний перехід у наступну колонку (заголовки тоді лишаються всередині колонки)
    if (lines.some((line) => line.break)) keyed.forEach((line) => (line.break || !groups.length ? groups.push(line.break ? [] : [line]) : groups[groups.length - 1].push(line)));
    else if (lines.some((line) => line.heading)) keyed.forEach((line) => (line.heading || !groups.length ? groups.push([line]) : groups[groups.length - 1].push(line)));
    else groups.push(keyed.slice(0, Math.ceil(lines.length / 2)), keyed.slice(Math.ceil(lines.length / 2)));
  }
  // Рядок з пропусками. Рядок { heading } — заголовок («Conversation 1»); з data.columns: 2 кожен заголовок починає колонку
  const renderLine = (line, i) => {
    if (line.heading) return <s.LinesHeading key={`h${i}`}>{line.heading}</s.LinesHeading>;
    const parts = line.text.split(GAP);
    return (
      <s.Line key={i}>
        {line.speaker && <s.Speaker>{line.speaker}:</s.Speaker>}
        <span>
          {parts.map((part, j) => {
            if (j % 2 === 0) return <Example key={j} text={part} />;
            const index = gapIds[`${i}-${j}`];
            return (
              <Gap
                key={j}
                index={index}
                slotMode={bank.length > 0 && !slide.data?.editable}
                answer={part}
                showAnswers={showAnswers}
                value={values[index] || ""}
                onChange={(value) => setValues((prev) => ({ ...prev, [index]: value }))}
                onFill={(dropped, from) => {
                  // перетягнули слово з банку або з іншого пропуску (тоді той пропуск звільняється)
                  if (dropped) {
                    setValues((prev) => ({ ...prev, ...(from !== null && from !== undefined && from !== index ? { [from]: "" } : {}), [index]: dropped }));
                    return;
                  }
                  if (picked) {
                    setValues((prev) => ({ ...prev, [index]: picked }));
                    setPicked(null);
                    return;
                  }
                  // клік по заповненому пропуску без обраного слова — слово повертається в банк
                  if (bank.length > 0 && values[index]) setValues((prev) => ({ ...prev, [index]: "" }));
                }}
              />
            );
          })}
        </span>
      </s.Line>
    );
  };

  // Картинка збоку (напр. ілюстрації з книжки до вправи) — лише якщо slide.image задано
  if (slide.image?.url) {
    return (
      <s.Split $withImage>
        <SideImage image={slide.image} />
        {renderFill()}
      </s.Split>
    );
  }
  return renderFill();

  function renderFill() {
    return (
    <s.Fill $compact={lines.length > 6 || columns === 2} $dense={slide.data?.dense}>
      {bank.length === 0 && <WordBox words={slide.data?.wordBox} />}
      {bank.length > 0 && (
        <s.Bank
          // слово, витягнуте з пропуску в банк, повертається в банк (пропуск звільняється)
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            const from = event.dataTransfer.getData("application/x-gap");
            if (from === "") return;
            event.preventDefault();
            setValues((prev) => ({ ...prev, [Number(from)]: "" }));
          }}
        >
          {bank.map((word) => (
            <s.BankWord
              key={word}
              draggable
              onDragStart={(event) => event.dataTransfer.setData("text/plain", word)}
              onClick={() => setPicked(picked === word ? null : word)}
              $active={picked === word}
              $used={usedWords.includes(norm(word)) && !showAnswers}
              title="Drag to a gap or click, then click a gap"
            >
              {word}
            </s.BankWord>
          ))}
          <s.Reset onClick={() => setValues({})} title={t("Reset")}>
            <LuRotateCcw size={18} />
          </s.Reset>
        </s.Bank>
      )}
      {/* data.example — приклад з книжки окремим виділеним рядком: "tall — {{short}}" */}
      {slide.data?.example && (
        <s.FillExample>
          <em>Example</em>
          <Example text={slide.data.example} />
        </s.FillExample>
      )}
      {groups.length > 1 ? (
        <s.Columns>
          {groups.map((group, g) => (
            <s.Lines key={g}>{group.map((line) => renderLine(line, line.i))}</s.Lines>
          ))}
        </s.Columns>
      ) : (
        <s.Lines>{lines.map(renderLine)}</s.Lines>
      )}
    </s.Fill>
  );
  }
}

// ---------- order ----------
function Order({ slide, showAnswers }) {
  const t = useT();
  const { items = [] } = slide.data || {};
  const shuffled = useMemo(() => shuffleStable(items, slide.id), [items, slide.id]);
  const [chosen, setChosen] = useSlideState(slide.id, "chosen", []);
  const result = showAnswers ? items : chosen;
  const done = result.length === items.length;
  useReport(slide.id, chosen.length === items.length ? items.filter((item, i) => chosen[i] === item).length : 0, items.length, chosen.length);
  // клік по пункту: ще не стоїть — ставимо наступним; уже стоїть (у списку чи в рамці) — прибираємо, решта зсувається
  const toggle = (item) => setChosen((prev) => (prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]));

  return (
    <s.OrderLayout>
      <s.OrderPool>
        {shuffled.map((item) => (
          <s.Chip
            key={item}
            $used={result.includes(item)}
            onClick={() => !showAnswers && toggle(item)}
          >
            {item}
          </s.Chip>
        ))}
        <s.Reset onClick={() => setChosen([])} title={t("Reset")}>
          <LuRotateCcw size={20} />
        </s.Reset>
      </s.OrderPool>
      <s.OrderList>
        {items.map((_, i) => {
          const value = result[i];
          const state = value && done ? (value === items[i] ? "ok" : "bad") : null;
          return (
            <s.OrderSlot
              key={i}
              $state={state}
              $filled={Boolean(value)}
              onClick={() => value && !showAnswers && toggle(value)}
              title={value && !showAnswers ? "Click to remove" : undefined}
              style={value && !showAnswers ? { cursor: "pointer" } : undefined}
            >
              <b>{i + 1}</b>
              {value || ""}
            </s.OrderSlot>
          );
        })}
      </s.OrderList>
    </s.OrderLayout>
  );
}

// ---------- listen_write ----------
// data: { answers, example?: { hear, write }, tip?, startAt? }
// example — плашка «чуєш → пишеш» (перший пункт аудіо як зразок), startAt — номер першого поля (як в аудіо)
function ListenWrite({ slide, showAnswers }) {
  const t = useT();
  const { answers = [], example, tip, startAt = 1 } = slide.data || {};
  const [values, setValues] = useSlideState(slide.id, "values", {});
  useReport(
    slide.id,
    answers.filter((answer, i) => values[i] && isRight(answer, values[i])).length,
    answers.length,
    Object.values(values).filter(Boolean).length
  );
  return (
    <s.ListenLayout>
      {(example || tip) && (
        <s.ListenExample>
          {example && (
            <span>
              <s.ExampleTag>{t("Example")}</s.ExampleTag>
              {t("You hear:")} <i>“{example.hear}”</i> <LuArrowRight size={22} /> {t("You write:")} <b>{example.write}</b>
            </span>
          )}
          {tip && <small>{tip}</small>}
        </s.ListenExample>
      )}
      <s.Dictation>
        {answers.map((answer, i) => (
          <s.DictationItem key={i}>
            <b>{i + startAt}</b>
            <Gap
              answer={answer}
              showAnswers={showAnswers}
              value={values[i] || ""}
              onChange={(value) => setValues((prev) => ({ ...prev, [i]: value }))}
            />
          </s.DictationItem>
        ))}
      </s.Dictation>
    </s.ListenLayout>
  );
}

// ---------- phrases ----------
function Phrases({ slide }) {
  const { phrases = [] } = slide.data || {};
  return (
    <s.PhraseGrid $count={phrases.length}>
      {phrases.map((phrase) => (
        <s.Phrase key={phrase.en}>
          <Hint ua={phrase.ua}>{phrase.en}</Hint>
        </s.Phrase>
      ))}
    </s.PhraseGrid>
  );
}

// ---------- game ----------
function Game({ slide }) {
  const { rules = [], examples = [] } = slide.data || {};
  return (
    <s.Split $withImage={examples.length > 0}>
      <s.Rules>
        {rules.map((rule, i) => (
          <s.Rule key={rule}>
            <b>{i + 1}</b>
            {rule}
          </s.Rule>
        ))}
      </s.Rules>
      {examples.length > 0 && (
        <s.Chat>
          {examples.map((example, i) => (
            <s.Bubble key={example} $right={i % 2 === 1}>
              {example}
            </s.Bubble>
          ))}
        </s.Chat>
      )}
    </s.Split>
  );
}

// ---------- quiz ----------
function Quiz({ slide, showAnswers }) {
  const t = useT();
  const { questions = [] } = slide.data || {};
  const [index, setIndex] = useSharedState(slide.id, "index", 0);
  const [picked, setPicked] = useSlideState(slide.id, "picked", {});
  const q = questions[index];
  const score = questions.filter((question, i) => picked[i] === question.answer).length;
  const finished = Object.keys(picked).length === questions.length;
  useReport(slide.id, score, questions.length, Object.keys(picked).length);

  if (!q) return null;
  return (
    <s.Quiz>
      <s.QuizProgress>
        {questions.map((_, i) => (
          <s.QuizDot key={i} $state={picked[i] === undefined ? null : picked[i] === questions[i].answer ? "ok" : "bad"} $current={i === index} />
        ))}
        {finished && (
          <s.QuizScore>
            {score} / {questions.length} <StarIcon size={22} />
          </s.QuizScore>
        )}
      </s.QuizProgress>
      <s.QuizQuestion>{q.q}</s.QuizQuestion>
      <s.QuizOptions>
        {q.options.map((option, i) => {
          const chosen = picked[index] === i;
          const reveal = showAnswers || picked[index] !== undefined;
          const state = reveal && i === q.answer ? "ok" : chosen ? "bad" : null;
          return (
            <s.QuizOption key={option} $state={state} onClick={() => picked[index] === undefined && setPicked((prev) => ({ ...prev, [index]: i }))}>
              {state === "ok" && <LuCheck size={22} />}
              {state === "bad" && <LuX size={22} />}
              {option}
            </s.QuizOption>
          );
        })}
      </s.QuizOptions>
      {index < questions.length - 1 && picked[index] !== undefined && (
        <s.QuizNext onClick={() => setIndex(index + 1)}>
          {t("Next")} <LuArrowRight size={20} />
        </s.QuizNext>
      )}
    </s.Quiz>
  );
}

// ---------- summary ----------
function Summary({ slide }) {
  const t = useT();
  // celebrate — конфеті й фанфара на кінець уроку (дитячі курси)
  const { learned = [], homeworkNote, celebrate } = slide.data || {};
  return (
    <s.Split $withImage={Boolean(homeworkNote)}>
      {celebrate && <Celebrate key={slide.id} />}
      <s.Learned>
        {learned.map((item) => (
          <s.LearnedItem key={item}>
            <s.GoalIcon>
              <LuCheck size={18} />
            </s.GoalIcon>
            {item}
          </s.LearnedItem>
        ))}
      </s.Learned>
      {homeworkNote && (
        <s.HomeworkCard>
          <StarIcon size={64} />
          <b>{homeworkNote}</b>
          <span>{t("+1 star for your homework")}</span>
        </s.HomeworkCard>
      )}
    </s.Split>
  );
}

// ---------- homework ----------
function Homework({ slide }) {
  const { tasks = [] } = slide.data || {};
  return (
    <s.Rules>
      {tasks.map((task, i) => (
        <s.Rule key={task.title}>
          <b>{i + 1}</b>
          <span>
            <strong>{task.title}</strong>
            {task.instruction && <small>{task.instruction}</small>}
          </span>
        </s.Rule>
      ))}
    </s.Rules>
  );
}

export const BLOCKS = {
  cover: Cover,
  talk: Talk,
  match_images: MatchImages,
  match_pairs: MatchPairs,
  cards: Cards,
  fill_gaps: FillGaps,
  order: Order,
  listen_write: ListenWrite,
  phrases: Phrases,
  game: Game,
  quiz: Quiz,
  summary: Summary,
  homework: Homework,
  ...EXTRA_BLOCKS,
  ...RICH_BLOCKS,
  ...KIDS_BLOCKS,
};
